import type { ComponentChildren } from 'preact';
import { createPortal } from 'preact/compat';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { navigate, useRoute } from '../lib/router';
import { BOOKS_CHANGED } from '../lib/changed';
import { recall, useRemembered } from '../lib/remember';
import { useNotice } from '../shell/notice';
import { useMatches } from '../shell/hooks';
import { ChipDivider, ChipRow, ChoiceChips, FilterChip } from '../ui/bits';
import { BookCell, ListRow, ListTable, type Col } from '../ui/ListTable';
import { arriveAt } from '../ui/arrive';
import { SectionFold, SectionFoldAll, openSectionFold } from '../ui/SectionFold';
import { Segmented } from '../ui/Segmented';
import { ListFoot } from '../ui/ListFoot';
import { useBookEdits } from '../book/actions';
import { loadBook } from '../book/load';
import { LoanSteps } from '../book/library';
import { FinishSheet, StartSheet, StopSheet } from '../book/reading';
import { addDays, bookLabel, currentLoan, dayText, openTargets, todayIn } from '../book/model';
import type { BookData } from '../book/types';
import { loadDetails, loadReferenceHeads, type Details, type Shelves } from '../shelves/load';
import type { ShelfBook } from '../shelves/model';
import {
  COMING_WORDS,
  ESTIMATES,
  HOLD_FORMATS,
  comingUp,
  holdsToPlace,
  libbySearch,
  librarySections,
  loanFormat,
  loanLine,
  type LibRow,
} from './library';
import { loadLibraryEvents, loadLibraryFacts, type LibraryFacts, type LibraryInfo } from './libraryLoad';
import { HIST_KINDS, byBook, byMonth, cleanKinds, kindCounts, monthName, stepDetail, stepName, stepTone, yearSummary, type HistEvent, type HistKind } from './history';
import { placeHold, setNotFromLibrary } from './actions';
import { myBooksTab } from './model';
import { COUNT_WORDS, NEEDS_YOU, STATE_NAMES, needsYou, type NeedsKey } from '../lib/libraryStates';
import './my-books.css';

// My books › Library copies (Phase 6.4 Part 2, step 1; agreed with Kristi Oct 31, laptop mockup
// in chat). Answers "where do my holds and loans stand, and what needs me?": the needs line (R2,
// Kristi Nov 2: the totals live in the section headings, no "Library now" line), Check & update, On loan, On hold, Holds to place (Up Next; Future reads and Not from the library
// in folds), Coming up (a right column on a laptop, a fold at the end on a phone) and the Your
// library line. Every library step is the book page's own (LoanSteps and its sheets), with Undo.
// Library history is step 2.
// Filters review (Kristi, Nov 2): signal chips replace the needs line (white with a dot for each
// needs-you state, a ring for Holds to place); "Check & update" is Check status; a hold you
// checked and quieted (Not ready yet ▾, 0051) sits grey at the foot of Check status.

const HOLDS_SHOWN = 4;
/** A signal chip on Library copies: a needs-you state, or Holds to place (worth a look). */
type Signal = NeedsKey | 'to_place';
const FUTURE_PAGE = 10;

type Load = { kind: 'loading' } | { kind: 'ready'; facts: LibraryFacts; extra: ShelfBook[]; events: HistEvent[] } | { kind: 'error'; message: string };

export function LibraryCopies({ wide, shelves, barSlot = null }: { wide: boolean; shelves: Shelves; barSlot?: HTMLElement | null }) {
  const today = todayIn();
  const [state, setState] = useState<Load>({ kind: 'loading' });
  const [details, setDetails] = useState<Map<string, Details>>(new Map());
  const [holdsShown, setHoldsShown] = useState(HOLDS_SHOWN);
  const [futureShown, setFutureShown] = useState(FUTURE_PAGE);
  // The signal chips that are on: they show only those books (F3), remembered on this device (F11).
  const [picked, setPicked] = useRemembered<Signal[]>('my_books:library:signals', []);

  const load = () =>
    Promise.all([loadLibraryFacts(), loadLibraryEvents()])
      .then(async ([facts, events]) => {
        // A book with a hold that isn't on a shelf here (a Reference book) is read on its own.
        const known = new Set(Object.values(shelves).flat().map((b) => b.id));
        const extra = facts.loans.some((l) => !known.has(l.bookId)) ? await loadReferenceHeads() : [];
        setState({ kind: 'ready', facts, extra, events });
      })
      .catch((e: unknown) => setState({ kind: 'error', message: e instanceof Error ? e.message : String(e) }));
  useEffect(() => {
    void load();
    const again = () => void load();
    window.addEventListener(BOOKS_CHANGED, again);
    return () => window.removeEventListener(BOOKS_CHANGED, again);
  }, [shelves]);

  const facts = state.kind === 'ready' ? state.facts : null;
  const sections = useMemo(() => (facts ? librarySections(shelves, facts.loans, today, state.kind === 'ready' ? state.extra : []) : null), [facts, shelves, today]);
  const toPlace = useMemo(() => (facts ? holdsToPlace(shelves, facts.copies, facts.loans) : null), [facts, shelves]);
  const coming = useMemo(() => (sections ? comingUp([...sections.check, ...sections.loan, ...sections.hold], today) : []), [sections, today]);

  // Authors and covers for the books on screen (the shelves read titles only).
  const onScreen = useMemo(() => {
    if (!sections || !toPlace) return [];
    const holds = sections.hold.slice(0, holdsShown);
    return [...sections.check, ...sections.quiet, ...sections.loan, ...holds].map((r) => r.book).concat(toPlace.upNext, toPlace.future.slice(0, futureShown), toPlace.notFrom);
  }, [sections, toPlace, holdsShown, futureShown]);
  useEffect(() => {
    const missing = onScreen.filter((b) => !details.has(b.id));
    if (!missing.length) return;
    let live = true;
    void loadDetails(missing, new Set())
      .then((d) => live && setDetails((old) => new Map([...old, ...d])))
      .catch(() => null);
    return () => {
      live = false;
    };
  }, [onScreen]);

  if (state.kind === 'loading') return <p class="shelves-quiet">Loading your library copies…</p>;
  if (state.kind === 'error') return <div class="heads-up">Couldn’t load your library copies: {state.message}</div>;
  const s = sections!;
  const p = toPlace!;
  const lib = state.facts.libraries[0] ?? null;
  const withDetails = (b: ShelfBook): ShelfBook => ({ ...b, ...(details.get(b.id) ?? {}), owned: b.owned });
  const holds = s.hold.slice(0, holdsShown);
  // Signal chips (F1, F2): one per needs-you state with books, then Holds to place, worth a look.
  const counts = new Map<NeedsKey, number>();
  for (const r of s.check) if ((NEEDS_YOU as readonly string[]).includes(r.why)) counts.set(r.why as NeedsKey, (counts.get(r.why as NeedsKey) ?? 0) + 1);
  // A remembered chip with nothing left under it is dropped, so it can't hide everything.
  const live = picked.filter((k) => (k === 'to_place' ? p.upNext.length > 0 : (counts.get(k) ?? 0) > 0));
  const on = live.length > 0;
  const checkRows = on ? s.check.filter((r) => live.includes(r.why as Signal)) : s.check;
  const showCheck = !on || checkRows.length > 0;
  const showPlace = !on || live.includes('to_place');
  const toggle = (k: Signal) => {
    const next = live.includes(k) ? live.filter((x) => x !== k) : [...live, k];
    setPicked(next);
    // Arriving (F11): open the section it leads to if folded, then scroll there.
    if (!live.includes(k)) {
      const id = k === 'to_place' ? 'place' : 'check';
      openSectionFold(id);
      arriveAt(`[data-fold="${id}"]`);
    }
  };

  const comingBox = (
    <ComingUpBox items={coming} />
  );
  return (
    <div class="mb-lib">
      <p class="queue-description">{myBooksTab('library').description}</p>
      {/* Signal chips replace the needs line (filters review, Kristi Nov 2); the tab's dot keeps its tooltip. */}
      {counts.size || p.upNext.length || on ? (
        <ChipRow label="What needs you, and what's worth a look">
          {NEEDS_YOU.map((k) => (
            <FilterChip key={k} label={STATE_NAMES[k]} count={counts.get(k) ?? 0} need={`Needs you: ${COUNT_WORDS[k](counts.get(k) ?? 0)}`} on={live.includes(k)} onToggle={() => toggle(k)} />
          ))}
          {counts.size && p.upNext.length ? <ChipDivider /> : null}
          <FilterChip label="Holds to place" count={p.upNext.length} worth={`Worth a look: ${COUNT_WORDS.to_place(p.upNext.length)}`} on={live.includes('to_place')} onToggle={() => toggle('to_place')} />
        </ChipRow>
      ) : null}
      {/* No "Library now" line (R2, L3, Kristi Nov 2): the totals are in the section headings. */}
      {barSlot ? createPortal(<SectionFoldAll />, barSlot) : (
        <div class="mb-lib-headrow">
          <SectionFoldAll />
        </div>
      )}
      <div class={wide ? 'mb-lib-cols' : ''}>
        <div class="mb-lib-main">
          {(s.check.length || s.quiet.length) && showCheck ? (
            <SectionFold store="my_books:library" id="check" title="Check status" count={checkRows.length}>
              <LibTable label="Check status" order="Most urgent first" wide={wide}>
              {checkRows.map((r) => (
                <LoanRow key={r.book.id} row={r} book={withDetails(r.book)} wide={wide} eager />
              ))}
              {/* Checked, not ready yet: grey at the foot until the quiet ends (Kristi, Nov 2). */}
              {on
                ? null
                : s.quiet.map((r) => (
                    <LoanRow key={r.book.id} row={r} book={withDetails(r.book)} wide={wide} eager quieted />
                  ))}
              </LibTable>
            </SectionFold>
          ) : null}
          {on ? null : (
            <>
          {s.loan.length ? (
            <SectionFold store="my_books:library" id="loan" title="On loan" count={s.loan.length}>
              <LibTable label="On loan" order="Due soonest first" wide={wide}>
                {s.loan.map((r) => (
                  <LoanRow key={r.book.id} row={r} book={withDetails(r.book)} wide={wide} eager />
                ))}
              </LibTable>
            </SectionFold>
          ) : null}
          {s.hold.length ? (
            <SectionFold store="my_books:library" id="hold" title="On hold" count={s.hold.length}>
              <LibTable label="On hold" order="Expected soonest first" wide={wide}>
                {holds.map((r) => (
                  <LoanRow key={r.book.id} row={r} book={withDetails(r.book)} wide={wide} />
                ))}
              </LibTable>
              <ListFoot
                shown={Math.min(holdsShown, s.hold.length)}
                total={s.hold.length}
                page={HOLDS_SHOWN}
                onMore={() => setHoldsShown(holdsShown + HOLDS_SHOWN)}
                onAll={() => setHoldsShown(s.hold.length)}
                onFewer={() => setHoldsShown(HOLDS_SHOWN)}
              />
            </SectionFold>
          ) : null}
            </>
          )}
          {showPlace ? (
          <SectionFold store="my_books:library" id="place" title="Holds to place" count={p.upNext.length} note="Up Next books with no copy and no hold" worth={p.upNext.length ? `Worth a look: ${COUNT_WORDS.to_place(p.upNext.length)}` : undefined}>
            {p.upNext.length ? (
              <LibTable label="Holds to place" order="In your Up Next order" place wide={wide}>
                {p.upNext.map((b) => <PlaceRow key={b.id} book={withDetails(b)} lib={lib} wide={wide} />)}
              </LibTable>
            ) : (
              <p class="shelves-quiet">Every Up Next book has a copy or a hold.</p>
            )}
            {p.future.length ? (
              <SectionFold store="my_books:library" id="place_future" title="Future reads with no copy and no hold" count={p.future.length} small closed>
                <LibTable label="Future reads with no copy and no hold" order="Highest Interest first" place wide={wide}>
                  {p.future.slice(0, futureShown).map((b) => (
                    <PlaceRow key={b.id} book={withDetails(b)} lib={lib} wide={wide} />
                  ))}
                </LibTable>
                <ListFoot
                  shown={Math.min(futureShown, p.future.length)}
                  total={p.future.length}
                  page={FUTURE_PAGE}
                  onMore={() => setFutureShown(futureShown + FUTURE_PAGE)}
                  onAll={() => setFutureShown(p.future.length)}
                  onFewer={() => setFutureShown(FUTURE_PAGE)}
                />
              </SectionFold>
            ) : null}
            {p.notFrom.length ? (
              <SectionFold store="my_books:library" id="place_not" title="Not from the library" count={p.notFrom.length} small closed>
                <LibTable label="Not from the library" order="Up Next, then Future reads" place wide={wide}>
                  {p.notFrom.map((b) => (
                    <PlaceRow key={b.id} book={withDetails(b)} lib={lib} wide={wide} notFrom />
                  ))}
                </LibTable>
              </SectionFold>
            ) : null}
          </SectionFold>
          ) : null}
          {on ? (
            <p class="mb-lib-showing">
              Showing only {live.map((k) => (k === 'to_place' ? 'holds to place' : STATE_NAMES[k].toLowerCase())).join(' and ')}.{' '}
              <button type="button" class="btn-link" onClick={() => setPicked([])}>
                Show everything
              </button>
            </p>
          ) : null}
          <LibraryHistory events={state.events} loans={state.facts.loans} wide={wide} />
          {!wide ? (
            <SectionFold store="my_books:library" id="coming" title="Coming up" count={coming.length} noun={['date', 'dates']} note="the next 30 days" closed>
              {comingBox}
            </SectionFold>
          ) : null}
          <LibraryLine libs={state.facts.libraries} />
        </div>
        {wide ? <aside class="mb-lib-side" aria-label="Coming up">{comingBox}</aside> : null}
      </div>
    </div>
  );
}

function ComingUpBox({ items }: { items: { on: string; kind: keyof typeof COMING_WORDS; titles: string[] }[] }) {
  return (
    <div class="mb-coming">
      <h2 class="mb-coming-head">Coming up · 30 days</h2>
      {items.length ? (
        <ul>
          {items.map((c) => (
            <li key={`${c.on}${c.kind}`}>
              <b>{dayText(c.on, todayIn())}</b> · {COMING_WORDS[c.kind]} · {c.titles.join(', ')}
            </li>
          ))}
        </ul>
      ) : (
        <p class="shelves-quiet">Nothing in the next 30 days.</p>
      )}
    </div>
  );
}

function LibraryLine({ libs }: { libs: LibraryInfo[] }) {
  if (!libs.length) return <p class="mb-lib-line">No lending library set up.</p>;
  return (
    <p class="mb-lib-line">
      {libs.map((l, i) => (
        <span key={l.id}>
          {i ? <br /> : null}
          Your library: {l.name}
          {l.apps.length ? ` · ${l.apps.join(', ')}` : ''} · borrow window {l.borrowWindow} days · Check Libby {l.checkDays} days before · loans {l.loanDays} days
        </span>
      ))}
    </p>
  );
}

/** Opens the book: the side panel on a laptop or iPad, the page on a phone. */
function useOpen(wide: boolean) {
  const { path } = useRoute();
  return (b: ShelfBook) => navigate(`/book/${bookLabel(b.label)}`, { state: { from: path }, keepScroll: wide });
}

type Sheet = { kind: 'start' } | { kind: 'finish' } | { kind: 'didnt_finish'; copy: string };

/**
 * A book with library holds or loans. Check & update and On loan rows read the full book straight
 * away (only a few) and show the book page's steps for each copy; On hold rows read it when ⋯ is
 * opened, adding Change the expected date and Let it go.
 */
function LoanRow({ row, book: head, wide, eager = false, quieted = false }: { row: LibRow; book: ShelfBook; wide: boolean; eager?: boolean; /** Checked, not ready yet: grey, with Check now. */ quieted?: boolean }) {
  const notice = useNotice();
  const today = todayIn();
  const phone = !useMatches('(min-width: 700px)');
  const [book, setBook] = useState<BookData | null>(null);
  const [open, setOpen] = useState(eager);
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [date, setDate] = useState('');
  const edits = useBookEdits(book, setBook, () => loadBook(head.label));
  useEffect(() => {
    if (!open || book) return;
    let live = true;
    loadBook(head.label)
      .then((b) => live && setBook(b))
      .catch((e: unknown) => notice({ message: `Couldn’t open ${head.title}: ${e instanceof Error ? e.message : String(e)}`, done: false }));
    return () => {
      live = false;
    };
  }, [open, head.label, row]);
  const lines = row.loans.map((l) => loanLine(l, row.reading, today) + (quieted && l.quietUntil ? ` · quiet until ${dayText(l.quietUntil, today)}` : ''));
  const reading = row.reading;
  // The library copies with a hold or loan, in the row's order.
  const copies = book ? row.loans.map((l) => book.copies.find((c) => c.id === l.copyId)).filter((c): c is NonNullable<typeof c> => Boolean(c && currentLoan(c))) : [];
  const targets = book && reading ? openTargets(book, phone) : [];
  const close = () => setSheet(null);
  const open_ = useOpen(wide);
  const needWords = needsYou(row.why) && !quieted ? `Needs you: ${COUNT_WORDS[row.why as NeedsKey](1).replace(/^1 /, '')}` : undefined;
  const steps = open ? (
    book ? (
      <div class="mb-row-steps">
        {targets.length ? (
          <span class="card-open-in">
            {targets.map((t) => (
              <a key={t.url} class="btn-book" href={t.url} target="_blank" rel="noopener noreferrer" title={t.sub}>
                {t.label}
              </a>
            ))}
          </span>
        ) : null}
        {copies.map((c) => (
          <div key={c.id} class="mb-copy-steps">
            {copies.length > 1 ? <span class="bk-label">{loanFormat(row.loans.find((l) => l.copyId === c.id)!)}</span> : null}
            <LoanSteps book={book} copy={c} edits={edits} onStart={() => setSheet({ kind: 'start' })} onFinished={() => setSheet({ kind: 'finish' })} onDidntFinish={() => setSheet({ kind: 'didnt_finish', copy: c.id })} />
            {currentLoan(c)?.step === 'on_hold' ? (
              <div class="bk-buttons mb-hold-more">
                <label class="mb-date">
                  <span>Change the expected date</span>
                  <input type="date" value={date} onInput={(e) => setDate((e.target as HTMLInputElement).value)} />
                </label>
                <button
                  type="button"
                  class="btn-secondary"
                  disabled={!date}
                  onClick={() => void edits.act('loan_edit', { loan: currentLoan(c)!.id, field: 'expected_on', value: date }, `Expected ${dayText(date, today)}`).then(() => setDate(''))}
                >
                  Save the date
                </button>
                <button type="button" class="btn-link" onClick={() => void edits.act('loan_step', { copy: c.id, step: 'let_go', details: {} }, `Let the hold go: ${head.title}`)}>
                  Let it go
                </button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    ) : (
      <span class="shelves-quiet">Loading…</span>
    )
  ) : null;
  // On hold rows fold their steps, as everywhere else in the app (Kristi, Oct 31: was ⋯).
  const unfold = !eager ? (
    <button type="button" class="btn-secondary mb-steps-btn" aria-expanded={open} aria-label={`${open ? 'Fold' : 'Show'} the steps for ${head.title}`} onClick={() => setOpen(!open)}>
      Steps {open ? '▾' : '▸'}
    </button>
  ) : null;
  const state = (
    <span class="mb-row-state">
      {(reading ? ['Reading', ...lines] : lines).map((l) => (
        <span key={l} class="mb-row-line">
          {l}
        </span>
      ))}
    </span>
  );
  const actions = (
    <span class="mb-row-actions">
      {unfold}
      {steps}
    </span>
  );
  const cell = <BookCell title={head.title} author={head.author} cover={head.cover} onOpen={() => open_(head)} />;
  return (
    <>
      {/* A row of the section's table (filters review, Kristi Nov 2: the proposed board): the teal edge
          and dot while it needs you; on hold and quieted holds are grey. */}
      <ListRow
        data-row={head.id}
        need={needWords}
        quiet={row.why === 'on_hold'}
        cells={[cell, state, actions]}
        phone={{ book: cell, control: unfold, line: (
          <>
            {state}
            {steps}
          </>
        ) }}
      />
      {book && sheet
        ? createPortal(
            <div class="shelf-sheet">
              {sheet.kind === 'start' ? <StartSheet book={book} edits={edits} onClose={close} /> : null}
              {sheet.kind === 'finish' ? <FinishSheet book={book} edits={edits} onClose={close} /> : null}
              {sheet.kind === 'didnt_finish' ? <StopSheet book={book} edits={edits} onClose={close} loanCopy={sheet.copy} /> : null}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/** Library copies' sections are tables (R6; Kristi, Nov 2): the book, where it stands, its steps. */
const LOAN_COLS: Col[] = [
  { label: 'Book', width: 'minmax(180px, 1.4fr)', book: true },
  { label: 'Where it stands', width: 'minmax(150px, 1fr)' },
  { width: 'minmax(260px, 1.8fr)' },
];
const PLACE_COLS: Col[] = [
  { label: 'Book', width: 'minmax(180px, 1.4fr)', book: true },
  { label: 'On your list', width: 'minmax(130px, 0.8fr)' },
  { width: 'minmax(260px, 1.8fr)' },
];

/** A section's table, in its own order (the sorting rule: urgency or your list's sequence is the information). */
function LibTable({ label, order, place = false, wide, children }: { label: string; order: string; place?: boolean; wide: boolean; children: ComponentChildren }) {
  return (
    <ListTable label={label} cols={place ? PLACE_COLS : LOAN_COLS} order={order} narrow={!wide} class="mb-lib-table">
      {children}
    </ListTable>
  );
}

const shelfWords: Record<string, string> = { up_next: 'Up Next', future_reads: 'Future reads' };

/**
 * A hold to place (Kristi, Oct 31): Place a hold in Libby ↗ opens Libby and asks which format and
 * Libby's estimate; Save the hold makes it (Add a copy's Library loan, on hold). Didn't place it
 * closes the question. Not from the library stops suggesting the book; Suggest again brings it back.
 */
function PlaceRow({ book, lib, wide, notFrom = false }: { book: ShelfBook; lib: LibraryInfo | null; wide: boolean; notFrom?: boolean }) {
  const notice = useNotice();
  const today = todayIn();
  const [asking, setAsking] = useState(false);
  const [fmt, setFmt] = useState(HOLD_FORMATS[0]!.key);
  const [est, setEst] = useState('30');
  const [date, setDate] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const f = HOLD_FORMATS.find((x) => x.key === fmt)!;
    const e = ESTIMATES.find((x) => x.key === est)!;
    const expectedOn = e.days !== null ? addDays(today, e.days) : est === 'date' ? date || null : null;
    setBusy(true);
    if (await placeHold(notice, book, { format: f.format, listen: f.listen, expectedOn, libraryId: lib?.id ?? null })) setAsking(false);
    setBusy(false);
  };
  const open_ = useOpen(wide);
  const buttons = notFrom ? (
    <span class="mb-row-buttons">
      <button type="button" class="btn-secondary" disabled={busy} onClick={() => void setNotFromLibrary(notice, book, false)}>
        Suggest again
      </button>
    </span>
  ) : !asking ? (
    <span class="mb-row-buttons">
      <a class="btn-book" href={libbySearch(lib?.libbyCode ?? null, book.title, book.author)} target="_blank" rel="noopener noreferrer" onClick={() => setAsking(true)}>
        Place a hold in Libby ↗
      </a>
      <button type="button" class="btn-secondary" disabled={busy} onClick={() => void setNotFromLibrary(notice, book, true)}>
        Not from the library
      </button>
    </span>
  ) : null;
  const ask = asking ? (
    <div class="mb-ask" role="group" aria-label={`The hold for ${book.title}`}>
      <span class="mb-row-line">Placed it? Which format, and what’s Libby’s estimate?</span>
      {/* A few choices inside a form are small chips, not a menu (consistency pass, R4, Kristi Nov 1). */}
      <ChoiceChips class="mb-ask-chips" label="Format" small choices={HOLD_FORMATS.map((f) => [f.key, f.label] as const)} value={fmt} onPick={(k) => k && setFmt(k)} />
      <ChoiceChips class="mb-ask-chips" label="Libby’s estimate" small choices={ESTIMATES.map((x) => [x.key, x.label] as const)} value={est} onPick={(k) => k && setEst(k)}>
        {est === 'date' ? <input type="date" aria-label="Libby’s date" value={date} onInput={(e) => setDate((e.target as HTMLInputElement).value)} /> : null}
      </ChoiceChips>
      <span class="mb-ask-buttons">
        <button type="button" class="btn-main" disabled={busy || (est === 'date' && !date)} onClick={() => void save()}>
          Save the hold
        </button>
        <button type="button" class="btn-secondary" disabled={busy} onClick={() => setAsking(false)}>
          Didn’t place it
        </button>
      </span>
    </div>
  ) : null;
  const cell = <BookCell title={book.title} author={book.author} cover={book.cover} onOpen={() => open_(book)} />;
  const listed = [shelfWords[book.status ?? ''] ?? '', book.interest ? `Interest ${book.interest}` : ''].filter(Boolean).join(' · ');
  // Worth a look (Kristi, Nov 2): no teal edge; the ring is on the section's heading.
  return (
    <>
      <ListRow
        data-row={book.id}
        cells={[cell, <span class="mb-row-line">{listed}</span>, <span class="mb-row-actions">{buttons}{ask}</span>]}
        phone={{ book: cell, line: (
          <span class="mb-row-actions">
            <span class="mb-row-line">{listed}</span>
            {buttons}
            {ask}
          </span>
        ) }}
      />
    </>
  );
}

const HISTORY_PAGE = 20;
const KINDS_KEY = 'my_books:library:history_kinds';

/**
 * Library history (step 2; Kristi, Oct 31: open to start, By book first, read only). Fixing a
 * step (Change the date, This didn't happen) stays in the book page's edit mode.
 */
function LibraryHistory({ events, loans, wide }: { events: HistEvent[]; loans: LibraryFacts['loans']; wide: boolean }) {
  const today = todayIn();
  const day = (d: string) => dayText(d, today);
  const open = useOpen(wide);
  const [view, setView] = useRemembered<'book' | 'date'>('my_books:library:history_view', 'book');
  const [chosenKinds, setKinds] = useRemembered<HistKind[]>(KINDS_KEY, []);
  const filter = cleanKinds(chosenKinds);
  const counts = useMemo(() => kindCounts(events), [events]);
  const [shown, setShown] = useState(HISTORY_PAGE);
  const [opened, setOpened] = useState<string[]>([]);
  const books = useMemo(() => byBook(events, loans, filter, day), [events, loans, filter, today]);
  const months = useMemo(() => byMonth(events, filter), [events, filter]);
  const book = (b: { label: number; title: string; bookId: string }) => open({ id: b.bookId, label: b.label, title: b.title } as ShelfBook);
  const step = (e: HistEvent, withBook: boolean, withCopy = withBook) => {
    const more = [withCopy ? loanFormat(e) : '', stepDetail(e, day)].filter(Boolean).join(' · ');
    return (
      <div key={e.id} class="mb-step" role="row">
        <span class="mb-step-on" role="cell">{e.on ? day(e.on) : 'Date not recorded'}</span>
        <span role="cell">
          <span class={`pill mb-step-pill ${stepTone(e.kind)}`}>{stepName(e.kind)}</span>
        </span>
        <span class="mb-step-what" role="cell">
          {withBook ? (
            <button type="button" class="q-title mb-title" onClick={() => book(e)}>
              {e.title}
            </button>
          ) : null}
          {more ? <span class="mb-row-line">{withBook ? ` · ${more}` : more}</span> : null}
        </span>
      </div>
    );
  };
  return (
    <SectionFold store="my_books:library" id="history" title="Library history" count={books.length}>
      <div class="mb-pane">
      <div class="mb-hist-top">
        <p class="mb-lib-head mb-hist-year">{yearSummary(events, today)}</p>
        {/* Expand history (Kristi, Oct 31): opens or folds every book's steps; By book only. */}
        {view === 'book' && books.length ? (
          <button
            type="button"
            class="btn-link mb-foldall"
            onClick={() => setOpened(opened.length ? [] : books.slice(0, shown).map((b) => b.bookId))}
          >
            {opened.length ? 'Collapse history' : 'Expand history'}
          </button>
        ) : null}
      </div>
      {/* Filter chips, then the view switch at the right of the line (R3 P2, P4; Kristi Nov 2): none on = every step. */}
      <div class="mb-hist-controls">
        <ChipRow label="Which steps">
          {HIST_KINDS.map((k) => (
            <FilterChip
              key={k.key}
              label={k.label}
              count={counts[k.key]}
              on={filter.includes(k.key)}
              onToggle={() => {
                const now = cleanKinds(recall(KINDS_KEY, []));
                setKinds(now.includes(k.key) ? now.filter((x) => x !== k.key) : [...now, k.key]);
                setShown(HISTORY_PAGE);
              }}
            />
          ))}
        </ChipRow>
        <Segmented label="Show history" options={[['book', 'By book'], ['date', 'By date']]} value={view} onChange={setView} />
      </div>
      {!events.length ? (
        <p class="shelves-quiet">No library steps yet. Each hold and loan is kept here for good.</p>
      ) : view === 'book' ? (
        <>
          {!books.length ? <p class="shelves-quiet">Nothing like that yet.</p> : null}
          {books.slice(0, shown).map((b) => {
            const isOpen = opened.includes(b.bookId);
            return (
              <div key={b.bookId} class="mb-row quiet mb-hist-book" data-row={b.bookId}>
                <div class="mb-hist-sum">
                  <button type="button" class="mb-fold-arrow" aria-expanded={isOpen} aria-label={`${isOpen ? 'Fold' : 'Show'} the steps for ${b.title}`} onClick={() => setOpened(isOpen ? opened.filter((x) => x !== b.bookId) : [...opened, b.bookId])}>
                    {isOpen ? '▾' : '▸'}
                  </button>
                  <button type="button" class="q-title mb-title" onClick={() => book(b)}>
                    {b.title}
                  </button>
                  <span class="mb-row-line">
                    {' · '}
                    {[b.formats, b.summary].filter(Boolean).join(' · ')}
                  </span>
                </div>
                {isOpen ? <div class="mb-steps" role="table" aria-label={`Library history of ${b.title}`}>{b.steps.map((e) => step(e, false, b.several))}</div> : null}
              </div>
            );
          })}
          <ListFoot
            shown={Math.min(shown, books.length)}
            total={books.length}
            page={HISTORY_PAGE}
            noun={['book', 'books']}
            onMore={() => setShown(shown + HISTORY_PAGE)}
            onAll={() => setShown(books.length)}
            onFewer={() => setShown(HISTORY_PAGE)}
          />
        </>
      ) : (
        <>
          {!months.length ? <p class="shelves-quiet">Nothing like that yet.</p> : null}
          {months.map((m) => (
            <div key={m.month} class="mb-hist-month">
              <h3 class="mb-month">{monthName(m.month)}</h3>
              <div class="mb-steps" role="table" aria-label={monthName(m.month)}>
                {m.steps.map((e) => step(e, true))}
              </div>
            </div>
          ))}
        </>
      )}
      </div>
    </SectionFold>
  );
}
